import type { User } from '../../types';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from '../ui/Table';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { getInitials, formatDate } from '../../utils/helpers';
import { Eye, Ban, Check, ChevronUp, ChevronDown, ChevronsUpDown, Trash2 } from 'lucide-react';

interface UserTableProps {
  users: User[];
  onViewUser: (userId: string) => void;
  onToggleStatus: (user: User) => void;
  onDeleteUser?: (user: User) => void;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  onSort?: (field: string) => void;
}

function SortIcon({ field, sortBy, sortDir }: { field: string; sortBy?: string; sortDir?: 'asc' | 'desc' }) {
  if (sortBy !== field) return <ChevronsUpDown className="w-3 h-3 opacity-40 shrink-0" />;
  return sortDir === 'asc'
    ? <ChevronUp   className="w-3 h-3 text-[#3b82f6] shrink-0" />
    : <ChevronDown className="w-3 h-3 text-[#3b82f6] shrink-0" />;
}

export function UserTable({ users, onViewUser, onToggleStatus, onDeleteUser, sortBy, sortDir, onSort }: UserTableProps) {
  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'company':    return <Badge variant="info">Company</Badge>;
      case 'job_seeker': return <Badge variant="success">Job Seeker</Badge>;
      default:           return <Badge>Unknown</Badge>;
    }
  };

  const SH = ({ field, label, right }: { field: string; label: string; right?: boolean }) => (
    <TableHeader
      sortable
      onClick={() => onSort?.(field)}
      className={right ? 'text-right' : ''}
    >
      <span className={`inline-flex items-center gap-1 ${sortBy === field ? 'text-[#f1f5f9]' : ''}`}>
        {label}
        <SortIcon field={field} sortBy={sortBy} sortDir={sortDir} />
      </span>
    </TableHeader>
  );

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHead>
          <TableRow>
            <SH field="name"      label="User" />
            <SH field="userType"  label="Type" />
            <SH field="twinCount" label="Twins" />
            <TableHeader>Status</TableHeader>
            <SH field="createdAt" label="Joined" />
            <TableHeader className="text-right">Actions</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id} hover>
              <TableCell>
                <div className="flex items-center gap-3">
                  {user.avatar ? (
                    <img src={user.avatar} alt={user.name} className="w-9 h-9 rounded-xl object-cover" />
                  ) : (
                    <div className="w-9 h-9 rounded-xl gradient-blue flex items-center justify-center text-xs font-semibold text-white shrink-0">
                      {getInitials(user.name)}
                    </div>
                  )}
                  <div>
                    <div className="font-medium text-[#f1f5f9] text-sm">{user.name}</div>
                    <div className="text-xs text-[#64748b]">{user.email}</div>
                  </div>
                </div>
              </TableCell>
              <TableCell>{getTypeBadge(user.userType)}</TableCell>
              <TableCell>
                <span className="font-medium">{user.twinCount}</span>
              </TableCell>
              <TableCell>
                {user.disabled
                  ? <Badge variant="error">Disabled</Badge>
                  : <Badge variant="success">Active</Badge>}
              </TableCell>
              <TableCell className="text-[#94a3b8]">{formatDate(user.createdAt)}</TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={() => onViewUser(user.id)} icon={<Eye className="w-4 h-4" />}>
                    View
                  </Button>
                  <Button
                    variant={user.disabled ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => onToggleStatus(user)}
                    icon={user.disabled ? <Check className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                  >
                    {user.disabled ? 'Enable' : 'Disable'}
                  </Button>
                  {onDeleteUser && !user.isAdmin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDeleteUser(user)}
                      icon={<Trash2 className="w-4 h-4" />}
                      className="text-[#f87171] hover:text-[#fca5a5]"
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
